import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ArrowRight, Car, Pill, Shirt, Cpu, Wheat, Factory, 
  Zap, HardHat, ShoppingCart, Wine, Boxes, CheckCircle2,
  Users, Globe, Shield, Clock, Phone, Mail, Sprout
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const SectorsPage = () => {
  const { t } = useTranslation();
  
  const sectors = [
    {
      id: 'automotive',
      icon: Car,
      image: '/assets/sector-automotive.jpg'
    },
    {
      id: 'pharmaceutical',
      icon: Pill,
      image: '/assets/sector-coldchain.jpg'
    },
    {
      id: 'textile',
      icon: Shirt,
      image: '/assets/sector-textile.jpg'
    },
    {
      id: 'electronics',
      icon: Cpu,
      image: '/assets/sector-electronics.jpg'
    },
    {
      id: 'agricultural',
      icon: Sprout,
      image: '/assets/sector-agricultural.jpg'
    },
    {
      id: 'industrial',
      icon: Factory,
      image: '/assets/sector-industrial.jpg'
    },
    {
      id: 'energy',
      icon: Zap,
      image: '/assets/sector-oil-energy.jpg'
    },
    {
      id: 'construction',
      icon: HardHat,
      image: '/assets/sector-construction.jpg'
    }
  ];

  const whyChooseUs = [
    {
      icon: Users,
      titleKey: 'sectors.benefits.specialized.title',
      descKey: 'sectors.benefits.specialized.desc'
    },
    {
      icon: Globe,
      titleKey: 'sectors.benefits.temperature.title',
      descKey: 'sectors.benefits.temperature.desc'
    },
    {
      icon: Shield,
      titleKey: 'sectors.benefits.compliance.title',
      descKey: 'sectors.benefits.compliance.desc'
    },
    {
      icon: Clock,
      titleKey: 'sectors.benefits.efficiency.title',
      descKey: 'sectors.benefits.efficiency.desc'
    }
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="sectors-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden" data-testid="sectors-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 Q 25 25, 50 50 T 100 50' stroke='%23C7A24E' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 100px'
          }} />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-gold font-space text-sm tracking-widest uppercase pb-2 border-b-2 border-gold/60 mb-6">
              {t('sectors.badge')}
            </span>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('sectors.title')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed mb-10">
              {t('sectors.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/quote" data-testid="sectors-quote-btn">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 hover:shadow-xl hover:shadow-gold/25 px-8 py-4 rounded-lg font-semibold transition-all">
                  {t('hero.requestQuote')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="sectors-contact-btn">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg">
                  {t('cta.contactUs')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-20 md:py-28 bg-white" data-testid="sectors-grid">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('industries.title')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('industries.heading')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('sectors.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectors.map((sector, index) => {
              const IconComponent = sector.icon;
              const sectorName = t(`sectors.${sector.id}.name`);
              const sectorDesc = t(`sectors.${sector.id}.description`);
              const features = t(`sectors.${sector.id}.features`, { returnObjects: true });
              
              return (
                <motion.div
                  key={sector.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`sector-card-${sector.id}`}
                >
                  <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 hover:shadow-lg transition-all overflow-hidden group">
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={sector.image}
                        alt={sectorName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <div className="bg-gold/90 p-2 rounded-lg">
                          <IconComponent className="w-6 h-6 text-navy-900" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-space text-xl font-bold text-navy-900 mb-2">
                        {sectorName}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">
                        {sectorDesc}
                      </p>
                      <div className="space-y-2">
                        {Array.isArray(features) && features.map((capability) => (
                          <div key={`${sector.id}-${capability.slice(0, 15)}`} className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                            <span className="text-sm text-slate-700">{capability}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-28 bg-navy-900" data-testid="sectors-why-us">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('sectors.whyTitle')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-4">
              {t('sectors.whyHeading')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={`sector-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-navy-800 border border-navy-700 rounded-xl p-6 text-center"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/10 rounded-xl mb-4">
                    <IconComponent className="w-7 h-7 text-gold" />
                  </div>
                  <h3 className="font-space text-lg font-bold text-white mb-2">
                    {t(item.titleKey)}
                  </h3>
                  <p className="text-steel-400 text-sm">
                    {t(item.descKey)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gold-light via-gold to-gold-dark" data-testid="sectors-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('sectors.ctaTitle')}
            </h2>
            <p className="text-navy-800/80 text-lg mb-8">
              {t('sectors.ctaDescription')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/quote">
                <Button className="bg-navy-900 text-white hover:bg-navy-800 px-8 py-4 rounded-lg font-semibold">
                  {t('hero.requestQuote')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900/10 px-8 py-4 rounded-lg">
                  {t('cta.contactUs')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-navy-800" data-testid="sectors-contact">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-8 text-steel-400">
            <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
              <Mail className="w-5 h-5" />
              <span>info@elementarycargo.com</span>
            </a>
            <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
              <Phone className="w-5 h-5" />
              <span>+31 62 538 1824</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SectorsPage;
